import React, { useState, useEffect, useRef } from "react";
import "../../styles/Home/Testimonials.css";
import { fetchTopReviews } from "../../functions/Reviews";
import BlurText from "./BlurText"; // <-- import BlurText

function GoogleLogo() {
  return (
    <svg className="google-logo" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function StarRating() {
  return (
    <div className="star-rating">
      {[...Array(5)].map((_, i) => (
        <i key={i} className="fas fa-star star"></i>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [reviews, setReviews] = useState([]); // <-- state for reviews
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  useEffect(() => {
    // Fetch reviews from db on mount
    fetchTopReviews().then((data) => {
      setReviews(data); // no grouping
    });
  }, []);

  const handleAnimationComplete = () => {
    console.log('Testimonial animation completed!');
  };

  // Calculate the number of slides (windows)
  const slideCount = reviews.length > 3 ? reviews.length - 2 : 1;

  // Clamp activeIndex to valid range
  useEffect(() => {
    if (activeIndex > slideCount - 1) setActiveIndex(0);
  }, [slideCount, activeIndex]);

  const handlePrev = () => setActiveIndex((prev) => (prev === 0 ? slideCount - 1 : prev - 1));
  const handleNext = () => setActiveIndex((prev) => (prev === slideCount - 1 ? 0 : prev + 1));
  const handleIndicator = (idx) => setActiveIndex(idx);

  // Handle touch events for mobile swiping
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    // Minimum swipe distance (in px) to trigger navigation
    const minSwipeDistance = 50;
    const distance = touchStartX.current - touchEndX.current;
    
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) {
        // Swiped left, go to next
        handleNext();
      } else {
        // Swiped right, go to previous
        handlePrev();
      }
    }
  };

  // Auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
    }, 9000); // changed from 5000 to 9000 ms
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="testimonials-section">
      <div className="container">
        <h2 className="section-title">
          <BlurText
            text="What Our Travellers Say"
            className=""
            animateBy="words"
            direction="top"
            delay={120}
            stepDuration={0.4}
          />
          <br />
          <span className="highlight-text">
            <BlurText
              text="Testimonials"
              className=""
              animateBy="words"
              direction="top"
              delay={120}
              stepDuration={0.4}
            />
          </span>
        </h2>
        <div className="position-relative">
          {/* Carousel */}
          <div 
            onTouchStart={handleTouchStart} 
            onTouchEnd={handleTouchEnd}
          >
            <div className="carousel-inner">
              {slideCount > 0 && (
                <div
                  className="carousel-item active"
                  style={{ display: "block" }}
                >
                  <div className="row justify-content-center">
                    {reviews.slice(activeIndex, activeIndex + 3).map((t, i) => {
                      // Remove JS truncation logic
                      const stars = Number.isFinite(parseInt(t.rating, 10)) && parseInt(t.rating, 10) > 0 ? parseInt(t.rating, 10) : 5;
                      return (
                        <div className="col-lg-4 col-md-6 col-12" key={i}>
                          <div className="testimonial-card">
                            <div className="testimonial-text">
                              <span className="clamp-5">
                                {/* Use plain text for clamped review comment */}
                                {t.comment}
                              </span>
                              {t.link && (
                                <button
                                  className="btn btn-link"
                                  style={{
                                    color: "#afc15a",
                                    textDecoration: "underline",
                                    fontWeight: 500,
                                    fontSize: "0.9rem",
                                    display: "block",
                                    marginTop: "6px",
                                    marginLeft: 0,
                                    padding: 0,
                                    background: "none",
                                    border: "none"
                                  }}
                                  onClick={() => window.open(t.link, "_blank", "noopener,noreferrer")}
                                  tabIndex={0}
                                >
                                  <BlurText
                                    text="Read More..."
                                    className=""
                                    animateBy="words"
                                    direction="top"
                                    delay={60}
                                    stepDuration={0.3}
                                  />
                                </button>
                              )}
                            </div>
                            {/* Show star rating based on t.rating */}
                            <div className="star-rating">
                              {(() => {
                                const rating = parseInt(t.rating, 10);
                                const stars = Number.isFinite(rating) && rating > 0 ? rating : 5;
                                return [...Array(stars)].map((_, i) => (
                                  <i key={i} className="fas fa-star star"></i>
                                ));
                              })()}
                            </div>
                            <div className="user-profile">
                              <div className="user-avatar">
                                <BlurText
                                  text={t.avatar || (t.name ? t.name.split(" ").map(n => n[0]).join("").toUpperCase() : "")}
                                  className=""
                                  animateBy="letters"
                                  direction="top"
                                  delay={30}
                                  stepDuration={0.2}
                                />
                              </div>
                              <div className="user-info">
                                <div className="user-name">
                                  <BlurText
                                    text={t.name}
                                    className=""
                                    animateBy="words"
                                    direction="top"
                                    delay={60}
                                    stepDuration={0.3}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* Controls */}
            <button
              className="carousel-control-prev"
              type="button"
              onClick={handlePrev}
              aria-label="Previous"
              style={{ border: "none" }}
            >
              <span className="carousel-control-prev-icon" aria-hidden="true" />
            </button>
            <button
              className="carousel-control-next"
              type="button"
              onClick={handleNext}
              aria-label="Next"
              style={{ border: "none" }}
            >
              <span className="carousel-control-next-icon" aria-hidden="true" />
            </button>
            {/* Indicators */}
            <div className="carousel-indicators">
              {[...Array(slideCount)].map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={activeIndex === idx ? "active" : ""}
                  onClick={() => handleIndicator(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                >
                  {/* Optionally animate indicator dots */}
                  {/* <BlurText text="●" animateBy="letters" direction="top" delay={20} stepDuration={0.2} /> */}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}